import { createFileRoute, useParams, useRouter } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useIsTablet } from "@/hooks/use-mobile";
import { PageHeader } from "@/components/layout/PageHeaderSlot";
import { locationApi } from "@/lib/api/admin-api";
import { useLingui } from "@lingui/react/macro";
import {
  ArrowLeft,
  MoreHorizontal,
  SquarePen,
  Globe,
  Clock,
  UtensilsCrossed,
  CalendarDays,
  MapPin,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ActivityLogTable } from "@/components/activity/ActivityLogTable";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { InfoCard } from "@/components/common/InfoCard";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import type { Location } from "@/types/location";

export const Route = createFileRoute(
  "/(protected)/_layout/locations_/$locationId",
)({
  component: LocationDetailPage,
});

type Tab = "general" | "recipes" | "access" | "audit";

function LocationDetailPage() {
  const { t } = useLingui();
  const { locationId } = useParams({
    from: "/(protected)/_layout/locations_/$locationId",
  });
  const { isAuthenticated } = useAuth();
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<Tab>("general");
  const isTablet = useIsTablet();

  const isValidId = !!locationId;

  const { data: location, isLoading } = useQuery({
    queryKey: ["locations", locationId],
    queryFn: () => locationApi.getById(locationId),
    enabled: isAuthenticated && isValidId,
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5 p-5">
        <Skeleton className="h-6 w-40" />
        <div className="flex items-center gap-6">
          <Skeleton className="size-40 rounded-2xl shrink-0" />
          <div className="flex flex-col gap-3">
            <Skeleton className="h-12 w-72" />
            <Skeleton className="h-5 w-52" />
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
        <Skeleton className="h-10 w-full" />
        <div className="flex flex-col gap-5">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
        </div>
      </div>
    );
  }

  if (!location) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <MapPin className="h-12 w-12 text-(--color-text-secondary)/30" />
        <p className="text-(--color-text-secondary)">{t`Location not found`}</p>
        <Button variant="outline" onClick={() => router.history.back()}>
          {t`Back to Locations`}
        </Button>
      </div>
    );
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: "general", label: t`General Details` },
    { id: "recipes", label: t`Recipes` },
    { id: "access", label: t`Access Control` },
    { id: "audit", label: t`Audit Logs` },
  ];

  const fullAddress = [
    location.addressLine1,
    location.city,
    location.state,
    location.postalCode,
    location.country,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="flex flex-col gap-5 p-3">
      {isTablet && (
        <PageHeader>
          <div className="flex h-15.5 items-center gap-3 border-b border-(--color-border) px-5">
            <button
              type="button"
              onClick={() => router.history.back()}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-(--color-border) bg-(--color-surface-content) text-(--color-text-primary) transition-colors hover:bg-(--color-surface-muted)"
              title={t`Back`}
            >
              <ArrowLeft className="size-5" />
            </button>
            <h1 className="text-[1.375rem] font-extrabold tracking-tight leading-none text-(--color-text-primary) truncate">
              {location.name}
            </h1>
            <span
              className={cn(
                "inline-flex items-center rounded-full px-2.5 py-0.5 text-[0.6875rem] font-bold shrink-0",
                location.isActive
                  ? "bg-(--color-success-bg) text-(--color-success-text)"
                  : "bg-(--color-surface-muted) text-(--color-text-secondary)",
              )}
            >
              {location.isActive ? t`Active` : t`Inactive`}
            </span>
            <div className="ml-auto flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                className="h-10 rounded-[10px] px-4 border-(--shell-border) gap-2"
              >
                <MoreHorizontal className="size-5" />
                <span className="text-base font-medium">{t`More`}</span>
              </Button>
              <Button className="h-10 rounded-[10px] px-4 bg-(--color-action) hover:bg-(--color-action)/90 text-(--color-action-fg) gap-2">
                <SquarePen className="size-5" />
                <span className="text-base font-medium">{t`Edit location`}</span>
              </Button>
            </div>
          </div>
        </PageHeader>
      )}
      {/* Page header */}
      {!isTablet && (
        <div className="flex flex-col gap-2">
          {/* Back link */}
          <button
            type="button"
            onClick={() => router.history.back()}
            className="flex items-center gap-2 px-3 py-2 w-fit rounded-lg hover:bg-(--color-surface-accent) transition-colors"
          >
            <ArrowLeft className="size-5 text-(--color-text-secondary)" />
            <span className="text-base font-medium text-(--color-text-secondary)">{t`Back`}</span>
          </button>

          {/* Hero */}
          <div className="flex items-start justify-between px-3">
            <div className="flex items-center gap-6">
              {/* Photo */}
              <div className="size-40 shrink-0 rounded-2xl border-4 border-white shadow-lg overflow-hidden bg-(--color-surface-muted) flex items-center justify-center">
                <Globe className="size-16 text-(--color-text-secondary)/30" />
              </div>

              {/* Name + meta */}
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-4">
                  <h1 className="text-[48px] font-semibold text-(--color-text-primary) leading-none">
                    {location.name}
                  </h1>
                  <Badge
                    className={cn(
                      "rounded-full px-3 py-1 text-sm font-medium border-transparent shrink-0",
                      location.isActive
                        ? "bg-(--color-status-active) text-(--brand-white) hover:bg-(--color-status-active)/90"
                        : "bg-(--color-surface-chip-inactive) text-(--color-text-primary)",
                    )}
                  >
                    {location.isActive ? t`Active` : t`Inactive`}
                  </Badge>
                </div>
                <p className="text-base text-(--color-text-secondary)">
                  {[location.code, location.region?.name, location.timezone]
                    .filter(Boolean)
                    .join(" • ")}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="h-13 rounded-[10px] px-4 border-(--shell-border) gap-2"
              >
                <MoreHorizontal className="size-5" />
                <span className="text-base font-medium">{t`More`}</span>
              </Button>
              <Button className="h-13 rounded-[10px] px-4 bg-(--color-action) hover:bg-(--color-action)/90 text-(--color-action-fg) gap-2">
                <SquarePen className="size-5" />
                <span className="text-base font-medium">{t`Edit location`}</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Info cards */}
      <div className="flex gap-2 rounded-xl bg-(--color-surface-muted) p-2">
        <InfoCard
          icon={<Globe className="size-5 text-(--color-text-secondary)" />}
          label={t`Region`}
          value={location.region?.name ?? "—"}
        />
        <InfoCard
          icon={<Clock className="size-5 text-(--color-text-secondary)" />}
          label={t`Timezone`}
          value={location.timezone}
        />
        <InfoCard
          icon={<UtensilsCrossed className="size-5 text-(--color-text-secondary)" />}
          label={t`Recipes assigned`}
          value="—"
        />
        <InfoCard
          icon={<CalendarDays className="size-5 text-(--color-text-secondary)" />}
          label={t`Last updated`}
          value={format(new Date(location.updatedAt), "dd MMM yyyy")}
        />
      </div>

      {/* Divider */}
      <div className="border-t border-(--shell-border)" />

      {/* Tab bar */}
      <div className="flex gap-1 bg-(--color-surface-muted) rounded-md p-1 w-full">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap",
              activeTab === tab.id
                ? "bg-(--color-surface-content) font-medium text-(--color-text-primary) shadow-xs"
                : "font-normal text-(--color-text-secondary) hover:text-(--color-text-secondary)",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "general" && (
        <GeneralDetailsTab location={location} fullAddress={fullAddress} />
      )}
      {activeTab === "audit" && (
        <ActivityLogTable scope={{ entityType: "location", entityId: Number(locationId) }} showActor />
      )}
      {activeTab !== "general" && activeTab !== "audit" && (
        <div className="py-16 text-center text-sm text-(--color-text-secondary)">
          {t`Coming soon`}
        </div>
      )}
    </div>
  );
}

function FormRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <div className="flex gap-8 items-start">
        <span className="w-85 shrink-0 text-sm font-medium text-(--color-text-primary) leading-5">
          {label}
        </span>
        <div className="flex-1">{children}</div>
      </div>
      <div className="h-px bg-(--shell-border)" />
    </>
  );
}

function GeneralDetailsTab({
  location,
  fullAddress,
}: {
  location: Location;
  fullAddress: string;
}) {
  const { t } = useLingui();

  return (
    <div className="px-4">
      {/* Section header */}
      <div className="flex flex-col gap-5 pb-px">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-2xl font-medium text-(--color-text-primary) leading-7">
              {t`General Details`}
            </h2>
            <p className="text-sm text-(--color-text-secondary) leading-5">
              {t`View and manage location information.`}
            </p>
          </div>
          <Button
            variant="outline"
            className="h-10 rounded-[10px] px-4 border-(--shell-border) gap-2 shrink-0"
          >
            <SquarePen className="size-4" />
            <span className="text-sm font-medium text-(--color-text-primary)">{t`Edit`}</span>
          </Button>
        </div>
        <div className="h-px bg-(--shell-border)" />
      </div>

      {/* Form rows */}
      <div className="flex flex-col gap-5 mt-5">
        <FormRow label={t`Name`}>
          <span className="text-base font-medium text-(--color-text-primary)">{location.name}</span>
        </FormRow>

        <FormRow label={t`Code`}>
          <span className="text-base font-medium text-(--color-text-primary)">{location.code}</span>
        </FormRow>

        <FormRow label={t`Status`}>
          <Badge
            className={cn(
              "rounded-full px-3 py-1 text-sm font-medium border-transparent",
              location.isActive
                ? "bg-(--color-status-active) text-(--brand-white) hover:bg-(--color-status-active)/90"
                : "bg-(--color-surface-chip-inactive) text-(--color-text-primary)",
            )}
          >
            {location.isActive ? t`Active` : t`Inactive`}
          </Badge>
        </FormRow>

        <FormRow label={t`Region assignment`}>
          <span className="text-base font-medium text-(--color-text-primary)">
            {location.region?.name ?? "—"}
          </span>
        </FormRow>

        <FormRow label={t`Address`}>
          <span className="text-base font-medium text-(--color-text-primary)">
            {fullAddress || "—"}
          </span>
        </FormRow>

        <FormRow label={t`Timezone`}>
          <span className="text-base font-medium text-(--color-text-primary)">{location.timezone}</span>
        </FormRow>

        {location.phone && (
          <FormRow label={t`Phone`}>
            <span className="text-base font-medium text-(--color-text-primary)">{location.phone}</span>
          </FormRow>
        )}

        {location.email && (
          <FormRow label={t`Email`}>
            <span className="text-base font-medium text-(--color-text-primary)">{location.email}</span>
          </FormRow>
        )}
      </div>
    </div>
  );
}
